import { InMemoryCache, type InMemoryCacheConfig } from '@apollo/client/cache';
import {
	ApolloClient,
	type ApolloClientOptions,
	createHttpLink,
	from,
	split,
} from '@apollo/client/core';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import EXTENDED_SCHEMA from '@/lib/apollo/clientSideSchema.graphql';
const jwtToken = window.location.pathname.substring(1).split('/')[2];

/**
 * Create a new cache instance for Apollo Client.
 *
 * This is a separate function so that the same cache implementation can be
 * used in tests.
 *
 * https://www.apollographql.com/docs/react/caching/cache-configuration/
 */
export const createApolloCache = (config: InMemoryCacheConfig = {}) => {
	const cache = new InMemoryCache({
		// In order to make use of fragments with unions and interfaces, we
		// need to configure which types extend which interfaces
		// https://www.apollographql.com/docs/react/data/fragments/#using-fragments-with-unions-and-interfaces
		possibleTypes: {
			FilterInterface: [
				'NumberRangeFilter',
				'DateRangeFilter',
				'MultiSelectFilter',
				'SingleSelectFilter',
			],
		},
		typePolicies: {
			Quote: {
				keyFields: [],
				fields: {
					comments: {
						merge: true,
					},
				},
			},
		},
		...config,
	});

	return cache;
};

/** NOTE: this can be changed to queries to include */
const batchExcludeQueryNames: string[] = [];

/**
 * Create the Apollo Client instance so that it can be used in our micro
 * front-ends.
 */
export const createExternalApolloClient = async () => {
	/**
	 * The HTTP link configures the details of how Apollo Client makes
	 * requests.
	 *
	 * https://www.apollographql.com/docs/link/links/http/
	 */
	const httpLink = createHttpLink({
		uri: '/external/graphql',
		headers: {
			Authorization: 'Bearer ' + jwtToken,
		},
	});

	/**
	 * The HTTP link batching configures the details of how Apollo Client makes
	 * combined requests.
	 *
	 * https://www.apollographql.com/docs/react/api/link/apollo-link-batch-http
	 */
	const batchLink = new BatchHttpLink({
		uri: '/external/graphql',
		batchMax: 5,
		batchInterval: 50,
		headers: {
			Authorization: 'Bearer ' + jwtToken,
		},
	});

	/**
	 * Add query name to batchExcludeQueryNames to exclude query from batching
	 */
	const directionalLink = split(
		(operation) =>
			!batchExcludeQueryNames.includes(operation.operationName),
		batchLink,
		httpLink
	);

	const cache = createApolloCache();

	/**
	 * Final configuration output for creating an apollo client instance
	 * In production mode, client-side type definitions and resolvers should be omitted
	 * Otherwise we excessively bloat the final bundle with faker factories!
	 */
	let apolloOptions: ApolloClientOptions<unknown> = {
		link: from([directionalLink]),
		queryDeduplication: true,
		cache,
		connectToDevTools: process.env.NODE_ENV === 'development',
	};

	if (
		process.env.VITE_SCORO_ENV === 'development' ||
		process.env.VITE_SCORO_ENV === 'staging'
	) {
		apolloOptions = {
			...apolloOptions,
			typeDefs: EXTENDED_SCHEMA,
		};
	}
	const apollo = new ApolloClient(apolloOptions);

	return apollo;
};
